import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import {
  Users, FileText, Settings, Brain, Activity, BarChart3, Plus, Save, Loader2,
  Pencil, Trash2, ChevronDown, ChevronUp, Search, Shield, AlertTriangle, Check, UserPlus
} from 'lucide-react';
import { api, DOMAIN_NAMES } from '../../lib/api';
import { toast } from 'sonner';
import WaitlistManagement from './WaitlistManagement';

const DOMAINS = [
  'biological_stability', 'brain_network_performance', 'brainwave_regulation',
  'cognitive_patterns', 'belief_architecture', 'behavioral_execution', 'identity_alignment'
];

const ROLES = ['individual', 'guided_client', 'counselor', 'admin'];

// ========== Questions Tab ==========
function QuestionsTab({ questions, onRefresh }) {
  const [filter, setFilter] = useState('express');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    domain: 'biological_stability',
    text: '',
    assessment_type: 'express',
    weight: 1.0
  });

  const displayQuestions = filter === 'express'
    ? questions?.express_questions
    : filter === 'full'
    ? questions?.full_questions
    : questions?.custom_questions;

  const filtered = (displayQuestions || []).filter(q =>
    q.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddQuestion = async () => {
    if (!newQuestion.text.trim()) { toast.error('Question text is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/questions', newQuestion);
      toast.success('Question created');
      setShowAddForm(false);
      setNewQuestion({ domain: 'biological_stability', text: '', assessment_type: 'express', weight: 1.0 });
      onRefresh();
    } catch (e) {
      toast.error('Failed to create question');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Assessment Questions</CardTitle>
            <CardDescription>Manage diagnostic questions across all 7 domains</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} data-testid="add-question-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Question
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Question Form */}
        {showAddForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5 space-y-3"
          >
            <h4 className="font-medium text-sm text-primary">New Custom Question</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Domain</Label>
                <Select value={newQuestion.domain} onValueChange={v => setNewQuestion(p => ({ ...p, domain: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOMAINS.map(d => (
                      <SelectItem key={d} value={d}>{DOMAIN_NAMES[d]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Assessment Type</Label>
                <Select value={newQuestion.assessment_type} onValueChange={v => setNewQuestion(p => ({ ...p, assessment_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="express">Express</SelectItem>
                    <SelectItem value="full">Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Question Text</Label>
              <Textarea
                value={newQuestion.text}
                onChange={e => setNewQuestion(p => ({ ...p, text: e.target.value }))}
                placeholder="Enter the diagnostic question..."
                className="mt-1"
                data-testid="new-question-text"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddQuestion} disabled={saving} data-testid="save-question-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Question
              </Button>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="flex gap-1 bg-secondary/30 rounded-lg p-1">
            {['express', 'full', 'custom'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`filter-${f}`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                <span className="ml-1 opacity-70">
                  ({f === 'express' ? questions?.express_questions?.length : f === 'full' ? questions?.full_questions?.length : questions?.custom_questions?.length || 0})
                </span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search questions..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 h-9"
              data-testid="search-questions"
            />
          </div>
        </div>

        {/* Question List */}
        <div className="max-h-[500px] overflow-y-auto space-y-2 pr-1">
          {filtered.map((q, i) => (
            <div key={q.id || i} className="p-3 rounded-lg bg-secondary/30 border border-border/50 hover:border-border transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded">{q.id}</span>
                    <Badge variant="outline" className="text-[10px]">{DOMAIN_NAMES[q.domain] || q.domain}</Badge>
                  </div>
                  <p className="text-sm">{q.text}</p>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No questions found</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ========== Scoring Rules Tab ==========
function ScoringRulesTab({ scoringRules, onRefresh }) {
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSaveRule = async (rule) => {
    setSaving(true);
    try {
      await api.post('/admin/scoring-rules', rule);
      toast.success(`Scoring rule for ${DOMAIN_NAMES[rule.domain]} updated`);
      setEditingRule(null);
      onRefresh();
    } catch (e) {
      toast.error('Failed to update scoring rule');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="text-lg">Scoring Rules</CardTitle>
        <CardDescription>Configure neural friction thresholds and domain weights for each cognitive domain</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {scoringRules?.is_default && (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Using default scoring rules. Edit any rule to create custom overrides.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {scoringRules?.rules?.map((rule) => {
            const isEditing = editingRule?.rule_id === rule.rule_id;
            return (
              <div key={rule.rule_id} className={`p-4 rounded-lg border transition-colors ${
                isEditing ? 'border-primary/50 bg-primary/5' : 'border-border/50 bg-secondary/30'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-sm">{DOMAIN_NAMES[rule.domain] || rule.domain}</h4>
                  {!isEditing ? (
                    <Button variant="ghost" size="sm" onClick={() => setEditingRule({ ...rule })} data-testid={`edit-rule-${rule.domain}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditingRule(null)}>Cancel</Button>
                      <Button size="sm" onClick={() => handleSaveRule(editingRule)} disabled={saving} data-testid={`save-rule-${rule.domain}`}>
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[['threshold_mild', 'Mild'], ['threshold_moderate', 'Moderate'], ['threshold_high', 'High']].map(([key, label]) => (
                      <div key={key}>
                        <Label className="text-[10px] text-muted-foreground">{label}</Label>
                        <Input
                          type="number"
                          value={editingRule[key]}
                          onChange={e => setEditingRule(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                          className="h-8 text-sm font-mono"
                        />
                      </div>
                    ))}
                    <div className="col-span-3">
                      <Label className="text-[10px] text-muted-foreground">Weight</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={editingRule.weight}
                        onChange={e => setEditingRule(p => ({ ...p, weight: parseFloat(e.target.value) || 1 }))}
                        className="h-8 text-sm font-mono"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {[['threshold_mild', 'Mild', '#10B981'], ['threshold_moderate', 'Moderate', '#F59E0B'], ['threshold_high', 'High', '#EF4444']].map(([key, label, color]) => (
                        <div key={key}>
                          <p className="text-muted-foreground">{label}</p>
                          <p className="font-mono font-medium" style={{ color }}>{rule[key]}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Weight: <span className="font-mono">{rule.weight}</span>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ========== Belief Patterns Tab ==========
function BeliefPatternsTab({ beliefPatterns, onRefresh }) {
  const [editingPattern, setEditingPattern] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPattern, setNewPattern] = useState({
    name: '', description: '', trigger_domains: [], trigger_threshold: 50, influence_weight: 1.0
  });

  const handleSavePattern = async (pattern) => {
    setSaving(true);
    try {
      await api.post('/admin/belief-patterns', pattern);
      toast.success('Belief pattern updated');
      setEditingPattern(null);
      onRefresh();
    } catch (e) {
      toast.error('Failed to update belief pattern');
    } finally { setSaving(false); }
  };

  const handleAddPattern = async () => {
    if (!newPattern.name.trim()) { toast.error('Pattern name is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/belief-patterns', newPattern);
      toast.success('Belief pattern created');
      setShowAddForm(false);
      setNewPattern({ name: '', description: '', trigger_domains: [], trigger_threshold: 50, influence_weight: 1.0 });
      onRefresh();
    } catch (e) {
      toast.error('Failed to create belief pattern');
    } finally { setSaving(false); }
  };

  const toggleDomain = (domain, forNew = false) => {
    if (forNew) {
      setNewPattern(p => ({
        ...p,
        trigger_domains: p.trigger_domains.includes(domain)
          ? p.trigger_domains.filter(d => d !== domain)
          : [...p.trigger_domains, domain]
      }));
    } else if (editingPattern) {
      setEditingPattern(p => ({
        ...p,
        trigger_domains: (p.trigger_domains || []).includes(domain)
          ? p.trigger_domains.filter(d => d !== domain)
          : [...(p.trigger_domains || []), domain]
      }));
    }
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Belief Architecture Patterns</CardTitle>
            <CardDescription>Configure pattern detection rules and domain triggers</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} data-testid="add-pattern-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Pattern
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showAddForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5 space-y-3"
          >
            <h4 className="font-medium text-sm text-primary">New Belief Pattern</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Pattern Name</Label>
                <Input value={newPattern.name} onChange={e => setNewPattern(p => ({ ...p, name: e.target.value }))} data-testid="new-pattern-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Threshold</Label>
                  <Input type="number" value={newPattern.trigger_threshold} onChange={e => setNewPattern(p => ({ ...p, trigger_threshold: parseFloat(e.target.value) || 50 }))} />
                </div>
                <div>
                  <Label className="text-xs">Weight</Label>
                  <Input type="number" step="0.1" value={newPattern.influence_weight} onChange={e => setNewPattern(p => ({ ...p, influence_weight: parseFloat(e.target.value) || 1 }))} />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={newPattern.description} onChange={e => setNewPattern(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs mb-2 block">Trigger Domains</Label>
              <div className="flex flex-wrap gap-2">
                {DOMAINS.map(d => (
                  <button
                    key={d}
                    onClick={() => toggleDomain(d, true)}
                    className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                      newPattern.trigger_domains.includes(d)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    {DOMAIN_NAMES[d]}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddPattern} disabled={saving} data-testid="save-pattern-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Pattern
              </Button>
            </div>
          </motion.div>
        )}

        {beliefPatterns?.is_default && (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Using default belief patterns. Create custom patterns to override.
          </div>
        )}

        <div className="space-y-3">
          {beliefPatterns?.patterns?.map((pattern, i) => (
            <div key={i} className="p-4 rounded-lg bg-secondary/30 border border-border/50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium">{pattern.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{pattern.description}</p>
                  {pattern.primary_domains && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {pattern.primary_domains.map(d => (
                        <Badge key={d} variant="outline" className="text-[10px]">{DOMAIN_NAMES[d] || d}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingPattern(pattern)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ========== Report Templates Tab ==========
function ReportTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ section: '', content_blocks: [''] });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get('/admin/report-templates');
        setTemplates(res.data.templates || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchTemplates();
  }, []);

  const handleSaveTemplate = async () => {
    if (!newTemplate.section.trim()) { toast.error('Section name is required'); return; }
    const filteredBlocks = newTemplate.content_blocks.filter(b => b.trim());
    if (filteredBlocks.length === 0) { toast.error('At least one content block is required'); return; }
    setSaving(true);
    try {
      await api.post('/admin/report-templates', { section: newTemplate.section, content_blocks: filteredBlocks });
      toast.success('Template saved');
      setShowAddForm(false);
      setNewTemplate({ section: '', content_blocks: [''] });
      const res = await api.get('/admin/report-templates');
      setTemplates(res.data.templates || []);
    } catch (e) {
      toast.error('Failed to save template');
    } finally { setSaving(false); }
  };

  const addContentBlock = () => {
    setNewTemplate(p => ({ ...p, content_blocks: [...p.content_blocks, ''] }));
  };

  const updateBlock = (idx, val) => {
    setNewTemplate(p => ({
      ...p,
      content_blocks: p.content_blocks.map((b, i) => i === idx ? val : b)
    }));
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Report Templates</CardTitle>
            <CardDescription>Customize interpretation text blocks and report section content</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} data-testid="add-template-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Template
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showAddForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5 space-y-3"
          >
            <h4 className="font-medium text-sm text-primary">New Report Template</h4>
            <div>
              <Label className="text-xs">Section Name</Label>
              <Input
                value={newTemplate.section}
                onChange={e => setNewTemplate(p => ({ ...p, section: e.target.value }))}
                placeholder="e.g., executive_summary, neural_friction_analysis"
                data-testid="new-template-section"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Content Blocks</Label>
              {newTemplate.content_blocks.map((block, idx) => (
                <Textarea
                  key={idx}
                  value={block}
                  onChange={e => updateBlock(idx, e.target.value)}
                  placeholder={`Content block ${idx + 1}...`}
                  rows={2}
                />
              ))}
              <Button variant="outline" size="sm" onClick={addContentBlock}>
                <Plus className="mr-2 h-3 w-3" /> Add Block
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveTemplate} disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Template
              </Button>
            </div>
          </motion.div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" strokeWidth={1} />
            <p>No custom templates yet</p>
            <p className="text-sm mt-2">Create templates to customize report interpretation sections</p>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((t, i) => (
              <div key={i} className="p-4 rounded-lg bg-secondary/30 border border-border/50">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="font-mono text-xs">{t.section}</Badge>
                  <span className="text-[10px] text-muted-foreground">{t.content_blocks?.length || 0} blocks</span>
                </div>
                <div className="space-y-1">
                  {t.content_blocks?.map((block, j) => (
                    <p key={j} className="text-sm text-muted-foreground">{block}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ========== Users Tab ==========
function UsersTab({ users, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingRole, setUpdatingRole] = useState(null);

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRole(userId);
    try {
      await api.put(`/admin/users/${userId}/role?role=${newRole}`);
      toast.success('User role updated');
      onRefresh();
    } catch (e) {
      toast.error('Failed to update role');
    } finally { setUpdatingRole(null); }
  };

  const roleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'counselor': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'guided_client': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    }
  };

  return (
    <Card className="bg-card border-border/50">
      <CardHeader>
        <CardTitle className="text-lg">User Management</CardTitle>
        <CardDescription>View users and manage roles across the platform</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-users"
          />
        </div>

        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {filtered.map((user) => (
            <div key={user.user_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50 hover:border-border transition-colors">
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-sm">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={user.role || 'individual'}
                  onValueChange={v => handleRoleChange(user.user_id, v)}
                  disabled={updatingRole === user.user_id}
                >
                  <SelectTrigger className={`h-8 w-36 text-xs border ${roleBadgeColor(user.role)}`} data-testid={`role-select-${user.user_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map(r => (
                      <SelectItem key={r} value={r}>
                        <div className="flex items-center gap-2">
                          {r === 'admin' && <Shield className="h-3 w-3" />}
                          {r.replace('_', ' ')}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {updatingRole === user.user_id && <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-8">No users found</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ========== Main Admin Dashboard ==========
export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [scoringRules, setScoringRules] = useState(null);
  const [beliefPatterns, setBeliefPatterns] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, questionsRes, rulesRes, patternsRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/questions'),
        api.get('/admin/scoring-rules'),
        api.get('/admin/belief-patterns'),
        api.get('/admin/users')
      ]);
      setStats(statsRes.data);
      setQuestions(questionsRes.data);
      setScoringRules(rulesRes.data);
      setBeliefPatterns(patternsRes.data);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground">Manage assessments, scoring rules, and platform content</p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-400' },
            { label: 'Assessments', value: stats.total_assessments, icon: FileText, color: 'text-emerald-400' },
            { label: 'Reports', value: stats.total_reports, icon: BarChart3, color: 'text-amber-400' },
            { label: 'Questions', value: (questions?.express_questions?.length || 0) + (questions?.full_questions?.length || 0), icon: Brain, color: 'text-purple-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="bg-card border-border/50">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
                    <p className="text-3xl font-bold mt-2 font-mono" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>{value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${color} opacity-50`} strokeWidth={1.5} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Admin Tabs */}
      <Tabs defaultValue="waitlist" className="space-y-6">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="waitlist" data-testid="tab-waitlist">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />Waitlist
          </TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">Questions</TabsTrigger>
          <TabsTrigger value="scoring" data-testid="tab-scoring">Scoring</TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns">Patterns</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="waitlist">
          <WaitlistManagement />
        </TabsContent>
        <TabsContent value="questions">
          <QuestionsTab questions={questions} onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="scoring">
          <ScoringRulesTab scoringRules={scoringRules} onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="patterns">
          <BeliefPatternsTab beliefPatterns={beliefPatterns} onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="templates">
          <ReportTemplatesTab />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab users={users} onRefresh={fetchData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
